import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { map } from 'lodash'

import CarouselDots from '@/components/base/carousel-dots'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'

import ColumnsCard from '../base/columns-card'
import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type NewsroomProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    collection: {
      pretitle: string
      title: string
      paragraph: string
      media: WPMedia
      link: WPLink
    }[]
  }>

const Newsroom = ({ collection, ...props }: NewsroomProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    active: true,
    breakpoints: {
      '(min-width: 768px)': { active: false }
    }
  })

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <Section {...props}>
      <Container>
        <Grid className="gap-y-[5rem]">
          <div
            ref={emblaRef}
            className={cn(
              'col-span-2 md:col-span-6 lg:col-span-12',
              '-mx-margin overflow-hidden px-margin'
            )}
          >
            <div
              className={cn(
                '-ml-margin flex touch-pan-y',
                'md:ml-0 md:grid md:grid-cols-6 md:gap-x-gutter lg:grid-cols-12'
              )}
            >
              {map(collection, ({ pretitle, title, paragraph, link, media }, index) => (
                <ColumnsCard
                  key={`card-${index}`}
                  pretitle={pretitle}
                  title={title}
                  paragraph={paragraph}
                  image={media.image}
                  link={link?.url}
                  className={cn(
                    'min-w-0 shrink-0 grow-0 basis-full pl-margin',
                    'col-span-2 md:col-span-3 md:pl-0 lg:col-span-6'
                  )}
                />
              ))}
            </div>

            <CarouselDots
              count={collection?.length}
              selectedIndex={selectedIndex}
              className="mt-[3rem] md:hidden"
            />
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default Newsroom
